// ═══════════════════════════════════════════════════════════════
// ChapterWorkspace — Racine atelier d'écriture COM/Écrivain (S56 P3)
// Fetch book + chapters, gère currentChapterId, compose Nav + Editor.
// Persistance currentChapterId dans localStorage par livre.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useCallback } from 'react';
import ChapterNav from './ChapterNav';
import ChapterEditor from './ChapterEditor';
import TransitionWorkspace from './TransitionWorkspace';
import { getBook, getChapter } from '../../services/bookApi';

const lastChapterKey = (bookId) => `itriia_com_writer_current_chapter_${bookId}`;

const ChapterWorkspace = ({ bookId, projectId, onCurrentChapterChanged }) => {
    const [book, setBook] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [currentChapterId, setCurrentChapterId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!bookId) return;
        try {
            const data = await getBook(bookId);
            setBook(data.book);
            setChapters(data.chapters || []);
            setError(null);
            setLoading(false);
            // Resolve currentChapterId :
            // 1. Déjà sélectionné et encore valide ?
            // 2. Sinon localStorage
            // 3. Sinon 1er chapitre
            setCurrentChapterId(prev => {
                if (prev && (data.chapters || []).some(c => c.id === prev)) return prev;
                const stored = parseInt(localStorage.getItem(lastChapterKey(bookId)), 10);
                if (stored && (data.chapters || []).some(c => c.id === stored)) return stored;
                return data.chapters?.[0]?.id || null;
            });
        } catch (err) {
            setError(err.message || 'Erreur chargement livre');
            setLoading(false);
        }
    }, [bookId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (currentChapterId && bookId) {
            localStorage.setItem(lastChapterKey(bookId), String(currentChapterId));
            if (onCurrentChapterChanged) onCurrentChapterChanged(currentChapterId);
        }
    }, [currentChapterId, bookId, onCurrentChapterChanged]);

    // S57-G fix : listChapters ne renvoie pas content_text pour rester léger.
    // Quand on change de chapitre courant, on fetch la version complète via getChapter.
    const [fullCurrentChapter, setFullCurrentChapter] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!currentChapterId || !bookId) { setFullCurrentChapter(null); return; }
        getChapter(bookId, currentChapterId).then(res => {
            if (!cancelled && res?.chapter) setFullCurrentChapter(res.chapter);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [currentChapterId, bookId]);

    const handleChapterUpdated = useCallback((updated) => {
        setChapters(list => list.map(c => c.id === updated.id ? { ...c, ...updated } : c));
        // Synchronise aussi fullCurrentChapter si c'est le chapitre courant
        if (updated?.id && updated.content_text !== undefined) {
            setFullCurrentChapter(prev => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
        }
    }, []);

    // S58 — TransitionWorkspace : pair {fromId, toId} ou null
    const [transitionPair, setTransitionPair] = useState(null);
    const openTransition = useCallback((fromId, toId) => {
        setTransitionPair({ fromId, toId });
    }, []);
    const closeTransition = useCallback(() => setTransitionPair(null), []);

    // S58 — Cache local des analyses de transition par paire+tier.
    // Clé : `${fromId}:${toId}:${tier}`. Survit au close/reopen de la modal.
    // Invalidé pour tout chapitre modifié après apply (voir handleTransitionApplied).
    // Évite de payer 2× si on revient sur la même transition sans rien changer.
    const [transitionCache, setTransitionCache] = useState({});
    const getCachedTransition = useCallback((fromId, toId, tier) => {
        return transitionCache[`${fromId}:${toId}:${tier}`] || null;
    }, [transitionCache]);
    const putCachedTransition = useCallback((fromId, toId, tier, result) => {
        setTransitionCache(prev => ({ ...prev, [`${fromId}:${toId}:${tier}`]: { ...result, _cachedAt: Date.now() } }));
    }, []);
    const invalidateCacheForChapters = useCallback((...chapterIds) => {
        setTransitionCache(prev => {
            const next = {};
            const ids = new Set(chapterIds.filter(Boolean).map(String));
            for (const [key, value] of Object.entries(prev)) {
                const [from, to] = key.split(':');
                if (!ids.has(from) && !ids.has(to)) next[key] = value;
            }
            return next;
        });
    }, []);

    if (loading) {
        return (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8888a0', fontSize: 13 }}>
                Chargement du livre…
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10 }}>
                <div style={{ color: '#ef4444', fontSize: 13 }}>Erreur : {error}</div>
                <button onClick={load} style={{
                    padding: '6px 16px', borderRadius: 6,
                    background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)',
                    color: '#a78bfa', fontSize: 12, cursor: 'pointer',
                }}>Réessayer</button>
            </div>
        );
    }

    // Utilise la version complète (avec content_text) si chargée, sinon fallback sur la meta
    const currentChapterMeta = chapters.find(c => c.id === currentChapterId) || null;
    const currentChapter = fullCurrentChapter && fullCurrentChapter.id === currentChapterId
        ? { ...currentChapterMeta, ...fullCurrentChapter }
        : currentChapterMeta;

    // Chapitre suivant pour le bouton Transition du header
    const currentIndex = currentChapterId ? chapters.findIndex(c => c.id === currentChapterId) : -1;
    const nextChapterId = currentIndex >= 0 && currentIndex < chapters.length - 1
        ? chapters[currentIndex + 1].id
        : null;

    // Chapitres passés à TransitionWorkspace (meta + opening_hook/closing_beat)
    const transitionFrom = transitionPair
        ? chapters.find(c => c.id === transitionPair.fromId)
        : null;
    const transitionTo = transitionPair
        ? chapters.find(c => c.id === transitionPair.toId)
        : null;

    const handleTransitionApplied = (result) => {
        // Recharge les chapitres + invalide fullCurrentChapter pour re-fetch content
        load();
        setFullCurrentChapter(null);
        // Invalide le cache des transitions touchant ces 2 chapitres —
        // leur contenu a changé, tout diagnostic antérieur devient périmé.
        if (transitionPair) {
            invalidateCacheForChapters(transitionPair.fromId, transitionPair.toId);
        }
    };

    return (
        <div style={{
            flex: 1, display: 'flex', flexDirection: 'row',
            height: '100%', minHeight: 0, overflow: 'hidden',
            background: 'var(--card-bg)',
        }}>
            <ChapterNav
                book={book}
                projectId={projectId}
                chapters={chapters}
                currentChapterId={currentChapterId}
                onSelectChapter={setCurrentChapterId}
                onChaptersChanged={load}
                onBookUpdated={load}
                onOpenTransition={openTransition}
            />
            <ChapterEditor
                bookId={bookId}
                book={book}
                projectId={projectId}
                chapter={currentChapter}
                nextChapterId={nextChapterId}
                onOpenTransition={openTransition}
                onChapterUpdated={handleChapterUpdated}
                onChapterCreated={(newChap) => {
                    load();
                    if (newChap?.id) setCurrentChapterId(newChap.id);
                }}
            />
            {transitionPair && transitionFrom && transitionTo && (
                <TransitionWorkspace
                    bookId={bookId}
                    fromChapter={transitionFrom}
                    toChapter={transitionTo}
                    onClose={closeTransition}
                    onApplied={handleTransitionApplied}
                    getCachedResult={getCachedTransition}
                    putCachedResult={putCachedTransition}
                />
            )}
        </div>
    );
};

export default ChapterWorkspace;
